'use client';

import { useEffect } from 'react';
import { useSwrRecruiterCall } from '@hooks/useSwrApi/endpoints/recruiter-call';
import type { RecruiterCallView } from '@type/recruiter-call';

interface RecruiterCallResultProps {
  callId: number;
}

/**
 * Post-call panel: transcript + AI analysis for a completed RecruiterCall.
 * Polls every 5s until the transcript arrives (matching the async
 * completion pipeline — transcription typically takes 20-60s after Twilio
 * fires the recording-status webhook).
 */
export const RecruiterCallResult = ({ callId }: RecruiterCallResultProps) => {
  const { data, isLoading, mutate } = useSwrRecruiterCall({ id: callId });

  const transcriptReady = Boolean(data?.transcript);

  useEffect(() => {
    if (!data) return undefined;
    if (transcriptReady) return undefined;
    if (data.status !== 'completed') return undefined;
    const t = setInterval(() => { void mutate(); }, 5000);
    return () => clearInterval(t);
  }, [data, transcriptReady, mutate]);

  if (isLoading || !data) {
    return <div className="text-sm text-ink-medium">Loading call…</div>;
  }

  return (
    <div className="space-y-4 rounded-3xl border border-input/40 bg-surface p-5">
      <Header call={data} />
      {data.aiAnalysis && <AiAnalysisBlock analysis={data.aiAnalysis} />}
      {data.transcript && <TranscriptBlock transcript={data.transcript} />}
      {!transcriptReady && data.status === 'completed' && (
        <div className="text-sm text-ink-medium">
          Transcript is being generated…
        </div>
      )}
    </div>
  );
};

const Header = ({ call }: { call: RecruiterCallView }) => {
  const duration = call.durationSeconds ?? 0;
  const mm = Math.floor(duration / 60).toString().padStart(2, '0');
  const ss = (duration % 60).toString().padStart(2, '0');
  return (
    <div className="flex items-baseline justify-between border-b border-input/40 pb-3">
      <div>
        <div className="text-sm font-semibold text-ink">Recruiter call</div>
        <div className="mt-1 text-xs text-ink-medium">
          {call.startedAt ? new Date(call.startedAt).toLocaleString() : '—'}
          {' · '}
          {mm}:{ss}
        </div>
      </div>
      <div className="text-xs uppercase tracking-wide text-ink-medium">
        {call.status}
      </div>
    </div>
  );
};

const AiAnalysisBlock = ({ analysis }: { analysis: NonNullable<RecruiterCallView['aiAnalysis']> }) => (
  <section className="space-y-3">
    <h4 className="text-sm font-semibold text-ink">AI summary</h4>
    <p className="text-sm text-ink">{analysis.summary ?? '—'}</p>
    <dl className="grid grid-cols-2 gap-3 text-sm">
      <Field label="Interest" value={analysis.interestLevel} />
      <Field label="Availability" value={analysis.availability} />
      <Field label="Salary expectation" value={analysis.salaryExpectation} />
      <Field label="Next steps" value={analysis.nextSteps} />
      <Field label="Red flags" value={analysis.redFlags} className="col-span-2" />
    </dl>
  </section>
);

const Field = ({ label, value, className }: { label: string; value: string | null | undefined; className?: string }) => (
  <div className={className}>
    <dt className="text-xs uppercase tracking-wide text-ink-medium">{label}</dt>
    <dd className="mt-1 text-ink">{value ?? '—'}</dd>
  </div>
);

const TranscriptBlock = ({ transcript }: { transcript: NonNullable<RecruiterCallView['transcript']> }) => (
  <section className="space-y-2">
    <h4 className="text-sm font-semibold text-ink">Transcript</h4>
    <ol className="space-y-2 text-sm">
      {(transcript.turns ?? []).map((turn, idx) => (
        <li key={`${idx}-${turn.speaker ?? ''}`} className="flex gap-2">
          <span className="min-w-24 shrink-0 font-semibold text-ink">{turn.speaker ?? ''}:</span>
          <span className="text-ink">{turn.text ?? ''}</span>
        </li>
      ))}
    </ol>
    {transcript.summary && (
      <p className="mt-2 border-t border-input/40 pt-2 text-xs italic text-ink-medium">
        {transcript.summary}
      </p>
    )}
  </section>
);
